// The trooper picker's rows (TrooperPicker.jsx, PickerScreens.jsx): a profile
// hit with its crest and stats, a loadout on its unit's page, a unit to drill
// into.
import {crestForRow} from '../../army/crests.js';
import {extraLoadoutName} from '../../army/names.js';
import {RowCrest} from '../Crest.jsx';
import {loadoutTag} from '../../state/pickerView.js';
import {useT} from '../i18n.jsx';

// The loadout's weapons, the one the query matched first: several loadouts
// often share their main weapon and only differ further down the list.
/** @typedef {import('../../types.js').ProfileHit} ProfileHit */
/** @typedef {import('../../types.js').Army} Army */

/** @param {ProfileHit} hit */
function weaponList(hit) {
  const rest = hit.weapons.filter((w) => w !== hit.weapon);
  return [hit.weapon, ...rest].filter(Boolean).join(', ');
}

/** @param {{hit: ProfileHit, onPick: (hit: ProfileHit) => void, first?: boolean, color: string, army: Army | null}} props */
export function ProfileRow({hit, onPick, first, color, army}) {
  const t = useT();
  const extra = extraLoadoutName(hit.loadout, hit.unit) ?? loadoutTag(army, hit);
  return (
    <button type="button" className={`hit${first ? ' first' : ''}`} onClick={() => onPick(hit)}>
      <RowCrest slug={crestForRow(army, hit)} />
      <span className="t">{hit.unit}{extra && <span className="tag"> · {extra}</span>}</span>
      <span className={`w c-${color}`}>{weaponList(hit)}</span>
      <span className="m">{t('stat.bs')} {hit.bs ?? '—'} · {hit.swc} {t('picker.swc')} · {hit.points} {t('picker.pts')}{hit.loose && <span className="loose"> · {t('picker.closeMatch')}</span>}</span>
    </button>
  );
}

// A loadout on its unit's page: the unit name is the page title, so just the
// weapons (and any loadout name), with the points on the right.
/** @param {{hit: ProfileHit, onPick: (hit: ProfileHit) => void, color: string, army: Army | null}} props */
export function LoadoutRow({hit, onPick, color, army}) {
  const extra = extraLoadoutName(hit.loadout, hit.unit) ?? loadoutTag(army, hit);
  return (
    <button type="button" className="line-btn loadout" onClick={() => onPick(hit)}>
      <span><span className={`c-${color}`}>{weaponList(hit)}</span>{extra && <span className="tag"> · {extra}</span>}</span>
      <span className="r">{hit.points}</span>
    </button>
  );
}

/** @param {{u: import('../../types.js').UnitHit, onOpen: (u: import('../../types.js').UnitHit) => void}} props */
export function UnitRow({u, onOpen}) {
  return (
    <button type="button" className="line-btn" onClick={() => onOpen(u)}>
      <span>{u.short}</span><span className="r">{u.profileCount} ›</span>
    </button>
  );
}
